import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import { useLeaveQuotas } from '../hooks/useLeaveQuotas';
import { LeaveBalancePanel } from '../components/LeaveBalancePanel';
import { computeEmployeeBalances, DISPLAYED_LEAVE_TYPES, type LeaveRecord } from '../utils/leaveBalanceUtils';
import jsPDF from 'jspdf';
import { 
  ArrowLeft, 
  Calendar, 
  Send, 
  History, 
  Check, 
  Clock, 
  XCircle, 
  AlertCircle,
  Download 
} from 'lucide-react';

// --- TYPES ---
type LeaveType = 'cp' | 'maladie' | 'sans_solde' | 'formation' | 'autre';

interface Leave {
  id: string;
  created_at: string;
  employee_id: string;
  start_date: string;
  end_date: string;
  type: LeaveType;
  status: 'pending' | 'validated' | 'rejected';
  reason: string | null;
  rejection_reason: string | null;
  employee?: {
    full_name: string;
  };
}

// Interface pour les images chargées
interface LoadedImage {
    base64: string;
    width: number;
    height: number;
}

const LEAVE_LABELS: Record<LeaveType, string> = {
  cp: 'Congés Payés',
  maladie: 'Maladie / Accident',
  sans_solde: 'Sans Solde',
  formation: 'Formation',
  autre: 'Autre'
};

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: '#b45309', bg: '#fffbeb', icon: <Clock size={16} /> },
  validated: { label: 'Validé', color: '#15803d', bg: '#dcfce7', icon: <Check size={16} /> },
  rejected: { label: 'Refusé', color: '#b91c1c', bg: '#fee2e2', icon: <XCircle size={16} /> }
};

// --- UTILITAIRE PDF ---
const loadImage = (url: string): Promise<LoadedImage> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous'); 
    img.src = url;
    img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve({ 
            base64: dataURL, 
            width: canvas.width, 
            height: canvas.height 
        });
    };
    img.onerror = error => reject(error);
  });
};


export default function LeaveRequest() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // États
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pdfGeneratingId, setPdfGeneratingId] = useState<string | null>(null);
  
  // Formulaire
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [type, setType] = useState<LeaveType>('cp');
  const [reason, setReason] = useState('');

  // Soldes de l'employé connecté (lecture seule)
  const { quotas, leaves: allLeaves } = useLeaveQuotas();
  const myBalances = useMemo(() => {
    if (!user) return [];
    return computeEmployeeBalances(
      allLeaves as LeaveRecord[],
      quotas,
      user.id,
      DISPLAYED_LEAVE_TYPES,
    );
  }, [user, allLeaves, quotas]);

  // 1. Chargement Historique
  const fetchLeaves = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('leaves')
        .select('*, employee:employees!employee_id(full_name)')
        .eq('employee_id', user.id)
        .order('start_date', { ascending: false });

      if (error) throw error;
      
      if (data) setLeaves(data as unknown as Leave[]);
      
    } catch (error) {
      console.error("Erreur chargement congés:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  // 2. Génération du PDF
  const generateLeavePDF = async (leave: Leave) => {
    setPdfGeneratingId(leave.id);

    try {
        const [logoData, signData] = await Promise.all([
            loadImage('/logo.png'),
            loadImage('/sign.png')
        ]);

        const doc = new jsPDF();
        
        // --- LOGO ---
        const logoTargetWidth = 50;
        const logoAspectRatio = logoData.width / logoData.height;
        const logoTargetHeight = logoTargetWidth / logoAspectRatio;

        doc.addImage(logoData.base64, 'PNG', 20, 15, logoTargetWidth, logoTargetHeight); 

        // --- TITRE (Suppression du texte OGER ENERGIES) ---
        // On calcule la position Y pour être sûr d'être sous le logo
        // 15 (marge haut) + logoTargetHeight + 15 (espace)
        const headerStartY = 15 + logoTargetHeight + 15;

        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        // Le titre remonte pour prendre la place
        doc.text("JUSTIFICATIF DE DEMANDE D'ABSENCE", 20, headerStartY);
        
        doc.setDrawColor(200, 200, 200);
        doc.line(20, headerStartY + 5, 190, headerStartY + 5);
        
        // --- CONTENU ---
        let yPos = headerStartY + 20;

        doc.setFontSize(11);
        doc.setTextColor(100, 100, 100);
        doc.text(`Salarié :`, 20, yPos);
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(`${leave.employee?.full_name || 'N/A'}`, 60, yPos);

        yPos += 15;
        const lineHeight = 12;

        const addLine = (label: string, value: string) => {
            doc.setFontSize(11);
            doc.setTextColor(100, 100, 100);
            doc.text(label, 20, yPos);
            
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            doc.text(value, 80, yPos);
            yPos += lineHeight;
        };

        addLine("Type d'absence :", LEAVE_LABELS[leave.type] || leave.type.toUpperCase());
        addLine("Date de début :", new Date(leave.start_date).toLocaleDateString());
        addLine("Date de fin :", new Date(leave.end_date).toLocaleDateString());
        addLine("Date de la demande :", new Date(leave.created_at).toLocaleDateString());
        
        if (leave.reason) {
            addLine("Motif indiqué :", leave.reason);
        }

        // --- STATUT ---
        yPos += 10;
        doc.setFontSize(11);
        doc.setTextColor(100, 100, 100);
        doc.text("État de la demande :", 20, yPos);
        
        let statusText = "EN ATTENTE";
        let statusColor = [180, 83, 9];
        if (leave.status === 'validated') {
            statusText = "VALIDÉE PAR LA DIRECTION";
            statusColor = [21, 128, 61];
        } else if (leave.status === 'rejected') {
            statusText = "REFUSÉE";
            statusColor = [185, 28, 28];
        }
        
        doc.setFontSize(12);
        doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
        doc.text(statusText, 80, yPos);

        // --- SIGNATURE ---
        yPos += 30;
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text("Pour OGER ÉNERGIES :", 140, yPos);
        
        const signTargetWidth = 40;
        const signAspectRatio = signData.width / signData.height;
        const signTargetHeight = signTargetWidth / signAspectRatio;
        
        doc.addImage(signData.base64, 'PNG', 140, yPos + 5, signTargetWidth, signTargetHeight);

        // Footer
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text(`Document généré le ${new Date().toLocaleDateString()} via Centralis ERP.`, 20, 280);

        doc.save(`absence_${new Date(leave.start_date).toISOString().split('T')[0]}.pdf`);

    } catch (error) {
        console.error("Erreur lors de la génération du PDF :", error);
        toast.error("Impossible de générer le PDF.");
    } finally {
        setPdfGeneratingId(null);
    }
  };

  // 3. Envoi Demande
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    if (!startDate || !endDate) { toast.warning("Veuillez sélectionner les dates."); return; }
    if (new Date(endDate) < new Date(startDate)) { toast.warning("La date de fin ne peut pas être antérieure au début."); return; }

    setSubmitting(true);
    
    try {
      const { error } = await supabase.from('leaves').insert({
        employee_id: user.id,
        start_date: startDate,
        end_date: endDate,
        type: type,
        reason: reason || null,
        status: 'pending'
      });

      if (error) throw error;

      toast.success("Demande envoyée avec succès !");
      setStartDate(''); setEndDate(''); setReason(''); setType('cp');
      fetchLeaves();

    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      toast.error("Erreur : " + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '20px', paddingBottom:'100px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
        <button 
          onClick={() => navigate('/')} 
          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer' }}
        >
          <ArrowLeft size={20} color="#64748b" />
        </button>
        <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Mes Absences</h1>
      </div>

      {/* MES SOLDES — lecture seule, calculé depuis quotas + leaves */}
      <div style={{ background: 'white', padding: '20px 24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
        <h2 style={{ marginTop: 0, marginBottom: '16px', color: '#10b981', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ background: '#ecfdf5', padding: '8px', borderRadius: '8px' }}><Check size={20} /></div>
          Mes soldes
        </h2>
        <LeaveBalancePanel balances={myBalances} compact />
      </div>

      {/* FORMULAIRE */}
      <div style={{ background: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '40px', border:'1px solid #e2e8f0' }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2563eb', fontSize:'1.1rem', display:'flex', alignItems:'center', gap:'10px' }}>
          <div style={{ background:'#eff6ff', padding:'8px', borderRadius:'8px' }}><Calendar size={20} /></div>
          Nouvelle demande
        </h2>
        
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '20px' }}>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', fontWeight: '600', color: '#475569', fontSize:'0.9rem' }}>
              Du
              <input 
                type="date" 
                value={startDate} 
                onChange={e => setStartDate(e.target.value)} 
                style={{ padding: '12px', marginTop: '6px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', color:'#1e293b' }} 
                required 
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontWeight: '600', color: '#475569', fontSize:'0.9rem' }}>
              Au (inclus)
              <input 
                type="date" 
                value={endDate} 
                onChange={e => setEndDate(e.target.value)} 
                style={{ padding: '12px', marginTop: '6px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', color:'#1e293b' }} 
                required 
              />
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', fontWeight: '600', color: '#475569', fontSize:'0.9rem' }}>
            Type d'absence
            <select 
              value={type} 
              onChange={e => setType(e.target.value as LeaveType)} 
              style={{ padding: '12px', marginTop: '6px', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', fontSize: '1rem', color:'#1e293b' }}
            >
              {Object.entries(LEAVE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', fontWeight: '600', color: '#475569', fontSize:'0.9rem' }}>
            Motif (facultatif)
            <input 
              type="text" 
              value={reason} 
              onChange={e => setReason(e.target.value)} 
              placeholder="Ex: Mariage, Rdv médical..." 
              style={{ padding: '12px', marginTop: '6px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', color:'#1e293b' }} 
            />
          </label>

          <button 
            type="submit" 
            disabled={submitting} 
            style={{ 
              marginTop: '10px', padding: '14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '12px', 
              fontSize: '1rem', fontWeight: '600', cursor: submitting ? 'wait' : 'pointer', 
              boxShadow: '0 4px 6px rgba(37,99,235,0.2)', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px' 
            }}
          >
            {submitting ? 'Envoi...' : <>Envoyer la demande <Send size={18} /></>}
          </button>
        </form>
      </div>

      {/* HISTORIQUE */}
      <h3 style={{ color: '#475569', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom:'20px', display:'flex', alignItems:'center', gap:'10px' }}>
        <History size={20} /> Mon Historique
      </h3>
      
      {loading ? (
        <p style={{ textAlign: 'center', color: '#94a3b8' }}>Chargement...</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {leaves.length === 0 ? (
            <div style={{ padding: '40px', background: 'white', borderRadius: '16px', border:'1px dashed #cbd5e1', textAlign: 'center', color: '#94a3b8' }}>
              Aucune demande enregistrée.
            </div>
          ) : (
            leaves.map(leave => {
              const statusStyle = STATUS_CONFIG[leave.status];
              const labelType = LEAVE_LABELS[leave.type] || leave.type.toUpperCase();
              const isGeneratingThisPdf = pdfGeneratingId === leave.id;

              return (
                <div key={leave.id} style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap:'15px' }}>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={{ fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>
                        {labelType}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px' }}>
                        Du {new Date(leave.start_date).toLocaleDateString()} au {new Date(leave.end_date).toLocaleDateString()}
                        </div>
                        {leave.rejection_reason && (
                        <div style={{ marginTop:'8px', display:'flex', alignItems:'center', gap:'6px', color:'#b91c1c', fontSize:'0.85rem', fontWeight:'500' }}>
                            <AlertCircle size={14} /> Motif refus : {leave.rejection_reason}
                        </div>
                        )}
                    </div>
                    
                    <span style={{ background: statusStyle.bg, color: statusStyle.color, padding: '6px 12px', borderRadius: '20px', fontWeight: '600', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap:'6px' }}>
                        {statusStyle.icon} {statusStyle.label}
                    </span>
                  </div>
                  
                  <div style={{ borderTop:'1px solid #f1f5f9', paddingTop:'15px', display:'flex', justifyContent:'flex-end' }}>
                     <button 
                        onClick={() => generateLeavePDF(leave)}
                        disabled={isGeneratingThisPdf}
                        style={{
                            background: 'white',
                            border: '1px solid #cbd5e1',
                            borderRadius: '8px',
                            padding: '8px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: isGeneratingThisPdf ? 'wait' : 'pointer',
                            color: '#475569',
                            fontWeight: '500',
                            fontSize: '0.9rem',
                            transition: 'all 0.2s',
                            opacity: isGeneratingThisPdf ? 0.7 : 1
                        }}
                    >
                        {isGeneratingThisPdf ? (
                          <Clock size={16} /> 
                        ) : (
                          <Download size={16} />
                        )}
                         {isGeneratingThisPdf ? 'Génération...' : 'Télécharger le justificatif'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}